import React, { useEffect, useRef, useState } from 'react';

/**
 * OptimizedVideo - Performance-optimized video component
 * Features:
 * - Lazy loading (loads only when in viewport)
 * - Preload control
 * - Poster image support
 * - Autoplay only when visible
 * - Reduced motion support
 */
const OptimizedVideo = ({
  src,
  poster,
  className = '',
  autoPlay = false,
  loop = false,
  muted = true,
  playsInline = true,
  controls = false,
  preload = 'metadata',
  priority = false,
  onLoad,
  ...props
}) => {
  const videoRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || priority) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (autoPlay && videoElement.paused) {
            videoElement.play().catch(() => {
              // Autoplay failed, user interaction needed
            });
          }
        } else {
          if (autoPlay && !videoElement.paused) {
            videoElement.pause();
          }
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(videoElement);

    return () => {
      observer.disconnect();
    };
  }, [autoPlay, priority]);

  // Check for reduced motion preference
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches && autoPlay) {
      const videoElement = videoRef.current;
      if (videoElement && !videoElement.paused) {
        videoElement.pause();
      }
    }
  }, [autoPlay]);

  const handleLoadedData = (e) => {
    setIsLoaded(true);
    onLoad?.(e);
  };

  return (
    <video
      ref={videoRef}
      className={`${className} ${!isLoaded ? 'opacity-0' : 'opacity-100 transition-opacity duration-500'}`}
      poster={poster}
      autoPlay={priority ? autoPlay : false}
      loop={loop}
      muted={muted}
      playsInline={playsInline}
      controls={controls}
      preload={priority ? 'auto' : preload}
      onLoadedData={handleLoadedData}
      {...props}
    >
      {(priority || isInView) && (
        <source src={src} type="video/mp4" />
      )}
      Your browser does not support the video tag.
    </video>
  );
};

export default OptimizedVideo;
